import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";

export default function MenuManager() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "menu");
  const [items, setItems] = useState([]);
  const [openCat, setOpenCat] = useState(0);

  useEffect(() => { api.get("/settings/menu").then((r) => setItems((r.data.data && r.data.data.items) || [])).catch(() => {}); }, []);

  const save = async () => {
    try { await api.put("/settings/menu", { data: { items } }); toast.success("Menu saved — live on the site header"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setCat = (ci, patch) => setItems(items.map((c, i) => i === ci ? { ...c, ...patch } : c));
  const addCat = () => setItems([...items, { label: "New Category", columns: [{ title: "Column", items: [] }] }]);
  const delCat = (ci) => setItems(items.filter((_, i) => i !== ci));
  const moveCat = (ci, d) => { const j = ci + d; if (j < 0 || j >= items.length) return; const a = [...items]; [a[ci], a[j]] = [a[j], a[ci]]; setItems(a); };

  const setCol = (ci, coli, patch) => setCat(ci, { columns: items[ci].columns.map((c, i) => i === coli ? { ...c, ...patch } : c) });
  const addCol = (ci) => setCat(ci, { columns: [...items[ci].columns, { title: "Column", items: [] }] });
  const delCol = (ci, coli) => setCat(ci, { columns: items[ci].columns.filter((_, i) => i !== coli) });

  const setLink = (ci, coli, li, patch) => setCol(ci, coli, { items: items[ci].columns[coli].items.map((it, i) => i === li ? { ...it, ...patch } : it) });
  const addLink = (ci, coli) => setCol(ci, coli, { items: [...items[ci].columns[coli].items, { l: "New Link", to: "" }] });
  const delLink = (ci, coli, li) => setCol(ci, coli, { items: items[ci].columns[coli].items.filter((_, i) => i !== li) });

  return (
    <div>
      <AdminHeader title="Menu Manager" subtitle="Edit the header mega-menu categories, columns and links. Use an internal path (e.g. /markets) or a Link Manager key (e.g. live_register)."
        action={canEdit && <div className="flex gap-2"><button type="button" onClick={addCat} className="btn-outline-gold px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="add-category-btn"><Plus size={16} /> Category</button><button type="button" onClick={save} className="btn-gold px-4 py-2.5 text-sm" data-testid="menu-save">Save Menu</button></div>} />

      <div className="space-y-3">
        {items.map((cat, ci) => (
          <Panel key={ci} className="p-0">
            <div className="flex items-center gap-3 p-4 border-b border-[#203E70]">
              <div className="flex flex-col">
                <button onClick={() => moveCat(ci, -1)} disabled={ci === 0} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30"><ChevronUp size={15} /></button>
                <button onClick={() => moveCat(ci, 1)} disabled={ci === items.length - 1} className="text-slate-500 hover:text-[#D8AE4E] disabled:opacity-30"><ChevronDown size={15} /></button>
              </div>
              <input value={cat.label} onChange={(e) => setCat(ci, { label: e.target.value })} className="bg-[#06122E] border border-[#203E70] rounded px-3 py-2 text-sm text-white font-semibold flex-1" data-testid={`cat-label-${ci}`} />
              <button onClick={() => setOpenCat(openCat === ci ? -1 : ci)} className="btn-outline-gold px-3 py-2 text-xs" data-testid={`cat-toggle-${ci}`}>{openCat === ci ? "Collapse" : "Edit"}</button>
              <button onClick={() => delCat(ci)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`cat-delete-${ci}`}><Trash2 size={15} /></button>
            </div>

            {openCat === ci && (
              <div className="p-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {cat.columns.map((col, coli) => (
                    <div key={coli} className="rounded border border-[#203E70] p-3">
                      <div className="flex items-center gap-2 mb-3">
                        <input value={col.title} onChange={(e) => setCol(ci, coli, { title: e.target.value })} className="bg-[#06122E] border border-[#203E70] rounded px-2 py-1.5 text-xs text-white flex-1" placeholder="Column title" data-testid={`col-title-${ci}-${coli}`} />
                        <button onClick={() => delCol(ci, coli)} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                      </div>
                      <div className="space-y-2">
                        {col.items.map((it, li) => (
                          <div key={li} className="flex gap-1.5 items-center">
                            <input value={it.l} onChange={(e) => setLink(ci, coli, li, { l: e.target.value })} placeholder="Label" className="bg-[#06122E] border border-[#203E70] rounded px-2 py-1.5 text-xs text-white w-28" data-testid={`link-label-${ci}-${coli}-${li}`} />
                            <input value={it.to || ""} onChange={(e) => setLink(ci, coli, li, { to: e.target.value, key: undefined })} placeholder="/path" className="bg-[#06122E] border border-[#203E70] rounded px-2 py-1.5 text-xs text-white flex-1" />
                            <input value={it.key || ""} onChange={(e) => setLink(ci, coli, li, { key: e.target.value, to: undefined })} placeholder="link key" className="bg-[#06122E] border border-[#203E70] rounded px-2 py-1.5 text-xs text-white w-24" />
                            <button onClick={() => delLink(ci, coli, li)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={13} /></button>
                          </div>
                        ))}
                        <button onClick={() => addLink(ci, coli)} className="text-xs gold-text" data-testid={`add-link-${ci}-${coli}`}>+ Add link</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => addCol(ci)} className="btn-outline-gold px-3 py-1.5 text-xs">+ Add column</button>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#203E70]">
                  <TextInput label="Featured CTA Label (optional)" value={(cat.cta && cat.cta.l) || ""} onChange={(e) => setCat(ci, { cta: { ...(cat.cta || {}), l: e.target.value } })} testid={`cta-label-${ci}`} />
                  <TextInput label="Featured CTA Link Key" value={(cat.cta && cat.cta.key) || ""} onChange={(e) => setCat(ci, { cta: { ...(cat.cta || {}), key: e.target.value } })} testid={`cta-key-${ci}`} />
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>
    </div>
  );
}
